import classNames from "classnames";
import CiroBreadCrumbs from "../../components/shared/CiroBreadCrumbs";
import CiroContainer from "../../components/shared/CiroContainer";
import {
  AutopilotPreviewSteps_AutopilotSessionFragmentDoc,
  AutopilotSessionType,
  EnumPersonaSource,
  InternalAutopilotCuratedSearch_AutopilotCuratedSearchQuery,
  InternalAutopilotCuratedSearch_AutopilotCuratedSearchQueryVariables,
  InternalAutopilotCuratedSearch_AutopilotSessionsQuery,
  InternalAutopilotCuratedSearch_AutopilotSessionsQueryVariables,
  InternalAutopilotCuratedSearch_UpsertAutopilotCuratedSearchMutation,
  InternalAutopilotCuratedSearch_UpsertAutopilotCuratedSearchMutationVariables,
  InternalCuratedSearchFilters,
} from "../../__generated__/graphql";
import { gql, useLazyQuery, useMutation, useQuery } from "@apollo/client";
import AutopilotCuratedSearchContext, {
  AutopilotCuratedSearchTypeEnum,
} from "../../contexts/AutopilotCuratedSearchContext";
import { useContext, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useFeatureFlagEnabled } from "posthog-js/react";
import { hasFiltersApplied } from "../../components/autopilotCuratedSearch/curatedSearchUtils";
import CuratedSearchCriteriaAndFiltersContainer from "../../components/autopilotCuratedSearch/CuratedSearchCriteriaAndFiltersContainer";
import AutopilotCuratedInternalSearchContext, {
  AutopilotCuratedInternalSearchContext_InternalSearchFilters,
} from "../../contexts/AutopilotCuratedInternalSearchContext";
import AutopilotPreviewSteps, {
  AutopilotPreviewSteps_AutopilotSession,
} from "../../components/autopilotPreview/AutopilotPreviewSteps";
import InternalAutopilotHydrationContext from "../../contexts/InternalAutopilotHydrationContext";
import { useFragment as getFragmentData } from "../../__generated__";
import { AutopilotProvider } from "../../components/contexts/AutopilotProvider";
import CuratedSearchBuilderHeading from "../../components/autopilotCuratedSearch/CuratedSearchPrompt/CuratedSearchBuilderHeading";
import CapacityLimitMessage from "../../components/shared/CapacityLimitMessage";

const InternalAutopilotCuratedSearch_UpsertAutopilotCuratedSearch = gql`
  mutation InternalAutopilotCuratedSearch_UpsertAutopilotCuratedSearch(
    $input: UpsertAutopilotCuratedSearchInput!
  ) {
    upsertAutopilotCuratedSearch(input: $input) {
      success
      autopilotCuratedSearch {
        id
        internalCuratedSearch {
          filters {
            ...AutopilotCuratedInternalSearchContext_InternalSearchFilters
          }
        }
      }
    }
  }
  ${AutopilotCuratedInternalSearchContext_InternalSearchFilters}
`;

const InternalAutopilotCuratedSearch_AutopilotCuratedSearch = gql`
  query InternalAutopilotCuratedSearch_AutopilotCuratedSearch {
    personas {
      id
      name
      description
      source
    }
  }
`;

const InternalAutopilotCuratedSearch_AutopilotSessions = gql`
  query InternalAutopilotCuratedSearch_AutopilotSessions(
    $input: AutopilotSessionsInput!
  ) {
    autopilotSessions(input: $input) {
      id
      ...AutopilotPreviewSteps_AutopilotSession
    }
  }
  ${AutopilotPreviewSteps_AutopilotSession}
`;

interface IBuildUpsertInputVariables {
  searchId: number | null;
  promptText: string;
  scoringCriteriaValues: string[];
  savedFilters: InternalCuratedSearchFilters | null;
  remixParentId?: number | null;
}
const buildUpsertInputVariables = ({
  searchId,
  promptText,
  scoringCriteriaValues,
  savedFilters,
  remixParentId,
}: IBuildUpsertInputVariables) => {
  return {
    id: searchId,
    promptText,
    scoringCriteria: scoringCriteriaValues.map((c) => ({
      criterion: c,
    })),
    remixParentId,
    internalSearchFilters: {
      companyKeywords: savedFilters?.companyKeywords,
      jobTitles: savedFilters?.jobTitles,
      industries: savedFilters?.industries?.map((i) => ({
        label: i.label,
      })),
      geographies: savedFilters?.geographies?.map((g) => ({
        id: g.id,
        label: g.label,
      })),
      contactRegions: savedFilters?.contactRegions?.map((c) => ({
        label: c.label,
      })),
      contactCities: savedFilters?.contactCities?.map((c) => ({
        label: c.label,
      })),
      companyHQLocations: savedFilters?.companyHQLocations?.map((c) => ({
        id: c.id,
        label: c.label,
      })),
      companyHQRegions: savedFilters?.companyHQRegions?.map((c) => ({
        label: c.label,
      })),
      companyHQCities: savedFilters?.companyHQCities?.map((c) => ({
        label: c.label,
      })),
      companyHeadCountRange: {
        lowerBound: savedFilters?.companyHeadCountRange?.lowerBound,
        upperBound: savedFilters?.companyHeadCountRange?.upperBound,
      },
      companyNames: savedFilters?.companyNames?.map((c) => ({
        label: c.label,
      })),
      // TODO: Add back profile summary keywords when Proxycurl gets us an answer
      // on better indexing (CIR-3434)
      // profileSummaryKeywords: currentFilters?.profileSummaryKeywords,
      profileSummaryKeywords: null,
    },
  };
};

const InternalAutopilotCuratedSearch = ({
  onboarding = false,
}: {
  onboarding?: boolean;
}) => {
  const isCapacityLimitEnabled = useFeatureFlagEnabled(
    "internal-autopilot-capacity-limit",
  );

  const {
    prompt: selectedPrompt,
    setPrompt: setSelectedPrompt,
    setExpandPrompt,
    expandPrompt,
    savedFilters,
    setSavedFilters,
    scoringCriteriaValues,
    setScoringCriteriaValues,
    previewStep,
    setPreviewStep,
    remixPreviewStep,
    setRemixPreviewStep,
    curatedSearchId,
    setCuratedSearchId,
    remixCuratedSearchId,
    setRemixCuratedSearchId,
    remixPrompt,
    setRemixPrompt,
    remixedFilters,
    setRemixedFilters,
    remixContext,
    expandContext,
    expandScoringCriteriaValues,
    setExpandScoringCriteriaValues,
    specificCompanies,
    setSpecificCompanies,
    previewContactFeedback,
    setPreviewContactFeedback,
    remixPreviewContactFeedback,
    setRemixPreviewContactFeedback,
    resetHydrationValues,
  } = useContext(InternalAutopilotHydrationContext);

  const hydratedWithPreview = Boolean(curatedSearchId && previewStep);
  const [startedGeneratingFilters, setStartedGeneratingFilters] = useState(
    savedFilters !== null || Boolean(expandPrompt),
  );
  const [startedPreviewingResults, setStartedPreviewingResults] =
    useState(hydratedWithPreview);
  const [criteriaMinimized, setCriteriaMinimized] = useState(false);
  const [filtersMinimized, setFiltersMinimized] = useState(false);

  const hydratedWithRemixPreview = Boolean(
    remixCuratedSearchId && remixPreviewStep,
  );
  const [showRemixStep, setShowRemixStep] = useState(Boolean(remixPrompt));
  const [startedRemixingFilters, setStartedRemixingFilters] = useState(
    remixedFilters !== null,
  );
  const [remixCriteriaMinimized, setRemixCriteriaMinimized] = useState(false);
  const [remixFiltersMinimized, setRemixFiltersMinimized] = useState(false);
  const [startedPreviewingRemixResults, setStartedPreviewingRemixResults] =
    useState(hydratedWithRemixPreview);

  const {
    data: autopilotCuratedSearchData,
    loading: autopilotCuratedSearchLoading,
  } = useQuery<
    InternalAutopilotCuratedSearch_AutopilotCuratedSearchQuery,
    InternalAutopilotCuratedSearch_AutopilotCuratedSearchQueryVariables
  >(InternalAutopilotCuratedSearch_AutopilotCuratedSearch);

  const [getExistingAutopilotSessions, { data: autopilotSessionsData }] =
    useLazyQuery<
      InternalAutopilotCuratedSearch_AutopilotSessionsQuery,
      InternalAutopilotCuratedSearch_AutopilotSessionsQueryVariables
    >(InternalAutopilotCuratedSearch_AutopilotSessions);

  const [
    upsertAutopilotCuratedSearch,
    { loading: upsertAutopilotCuratedSearchLoading },
  ] = useMutation<
    InternalAutopilotCuratedSearch_UpsertAutopilotCuratedSearchMutation,
    InternalAutopilotCuratedSearch_UpsertAutopilotCuratedSearchMutationVariables
  >(InternalAutopilotCuratedSearch_UpsertAutopilotCuratedSearch);

  const suggestedSearchPrompts = useMemo(() => {
    if (!autopilotCuratedSearchData?.personas) return;
    return autopilotCuratedSearchData.personas
      .filter((persona) => persona.source === EnumPersonaSource.Ciro)
      .slice(0, 3)
      .map((persona) => persona.description ?? "");
  }, [autopilotCuratedSearchData?.personas]);

  const combinedLLMFeedback = useMemo(() => {
    const previewFeedback = previewStep?.llmFeedback ?? [];
    const remixFeedback = remixPreviewStep?.llmFeedback ?? [];
    const combinedFeedback = [...remixFeedback, ...previewFeedback];
    return combinedFeedback
      .filter((prompt): prompt is string => prompt !== null)
      .slice(0, 3);
  }, [previewStep, remixPreviewStep]);

  const onSaveFilters = async () => {
    if (!selectedPrompt) {
      toast.error("No search found");
      return;
    }
    if (!hasFiltersApplied({ filters: savedFilters, type: "internal" })) {
      toast.error("No filters applied");
      return;
    }

    if (remixPrompt) {
      setStartedPreviewingRemixResults(false);
    } else {
      setStartedPreviewingResults(false);
      setPreviewStep(null);
    }

    toast.success("Saving your search...");
    // Save the current filters and scoring criteria before making the API call
    // This preserves them in case the response tries to overwrite them
    const upsertVariables = remixPrompt
      ? buildUpsertInputVariables({
          searchId: remixCuratedSearchId,
          promptText: remixPrompt,
          scoringCriteriaValues,
          savedFilters: remixedFilters,
          remixParentId: curatedSearchId,
        })
      : buildUpsertInputVariables({
          searchId: curatedSearchId,
          promptText: selectedPrompt,
          scoringCriteriaValues,
          savedFilters,
        });

    await upsertAutopilotCuratedSearch({
      variables: {
        input: upsertVariables,
      },
      onCompleted: async (data) => {
        if (remixPrompt) {
          setRemixCuratedSearchId(
            data.upsertAutopilotCuratedSearch.autopilotCuratedSearch?.id ??
              null,
          );
          setRemixFiltersMinimized(true);
          setStartedPreviewingRemixResults(true);
        } else {
          setCuratedSearchId(
            data.upsertAutopilotCuratedSearch.autopilotCuratedSearch?.id ??
              null,
          );
          setFiltersMinimized(true);
          setStartedPreviewingResults(true);
        }
      },
    });

    await getExistingAutopilotSessions({
      variables: {
        input: {
          searchType: AutopilotSessionType.Internal,
          internalSearchFilters: upsertVariables.internalSearchFilters,
        },
      },
    });
  };

  const resetRemix = () => {
    setRemixPrompt("");
    setExpandPrompt("");
    setRemixCuratedSearchId(null);
    setRemixFiltersMinimized(false);
    setRemixCriteriaMinimized(false);
    setStartedRemixingFilters(false);
    setStartedPreviewingRemixResults(false);
    setRemixedFilters(null);
    setRemixPreviewStep(null);
    setRemixPreviewContactFeedback(new Map());
  };

  const reset = () => {
    resetHydrationValues({});
    resetRemix();
    setCriteriaMinimized(false);
    setFiltersMinimized(false);
    setStartedGeneratingFilters(false);
    setShowRemixStep(false);
  };

  if (isCapacityLimitEnabled) {
    return <CapacityLimitMessage />;
  }

  return (
    <AutopilotProvider>
      <AutopilotCuratedSearchContext.Provider
        value={{
          searchType: AutopilotCuratedSearchTypeEnum.Internal,
          selectedPrompt: selectedPrompt,
          setSelectedPrompt: (prompt) => {
            setSelectedPrompt(prompt || "");
          },
          specificCompanies,
          setSpecificCompanies,
          startedGeneratingFilters,
          setStartedGeneratingFilters,
          upsertAutopilotCuratedSearchLoading,
          remixPrompt: remixPrompt,
          setRemixPrompt: (prompt) => {
            setRemixPrompt(prompt || "");
          },
          remixPromptOptions: combinedLLMFeedback,
          setStartedRemixingFilters: (value) => {
            setStartedRemixingFilters(value);
          },
          expandScoringCriteriaValues,
          setExpandScoringCriteriaValues,
          setShowRemixStep: (value) => {
            setShowRemixStep(value);
          },
          resetRemix,
          reset: () => {
            reset();
          },
          scoringCriteriaValues,
          setScoringCriteriaValues,
        }}
      >
        <AutopilotCuratedInternalSearchContext.Provider
          value={{
            onSaveFilters,
            savedFilters,
            setSavedFilters,
          }}
        >
          <CiroContainer className={classNames("bg-zinc-100")}>
            <CiroBreadCrumbs
              values={[
                "Autopilot",
                onboarding ? "Start searching" : "Search Ciro Database",
              ]}
              href="/autopilot"
            />
            <CuratedSearchBuilderHeading
              selectedPrompt={selectedPrompt}
              expandPrompt={expandPrompt}
              startedGeneratingFilters={startedGeneratingFilters}
              reset={reset}
              title="Search Ciro Database"
            />

            <CuratedSearchCriteriaAndFiltersContainer
              criteriaMinimized={criteriaMinimized}
              filtersMinimized={filtersMinimized}
              setCriteriaMinimized={setCriteriaMinimized}
              setFiltersMinimized={setFiltersMinimized}
              selectedPrompt={selectedPrompt}
              expandPrompt={expandPrompt}
              setSelectedPrompt={setSelectedPrompt}
              startedGeneratingFilters={startedGeneratingFilters}
              setStartedGeneratingFilters={setStartedGeneratingFilters}
              loading={autopilotCuratedSearchLoading}
              remixContext={expandContext}
              savedFilters={savedFilters}
              setSavedFilters={setSavedFilters}
              suggestedPrompts={suggestedSearchPrompts}
            />

            {startedPreviewingResults && (
              <AutopilotPreviewSteps
                curatedSearchId={curatedSearchId}
                previewStep={previewStep}
                setPreviewStep={setPreviewStep}
                previewContactFeedback={previewContactFeedback}
                setPreviewContactFeedback={setPreviewContactFeedback}
                existingAutopilotSession={
                  getFragmentData(
                    AutopilotPreviewSteps_AutopilotSessionFragmentDoc,
                    autopilotSessionsData?.autopilotSessions[0],
                  ) ?? null
                }
              />
            )}
            {showRemixStep && (
              <>
                <CuratedSearchCriteriaAndFiltersContainer
                  criteriaMinimized={remixCriteriaMinimized}
                  filtersMinimized={remixFiltersMinimized}
                  setCriteriaMinimized={setRemixCriteriaMinimized}
                  setFiltersMinimized={setRemixFiltersMinimized}
                  selectedPrompt={remixPrompt}
                  setSelectedPrompt={setRemixPrompt}
                  isRemixStep={true}
                  startedGeneratingFilters={startedRemixingFilters}
                  setStartedGeneratingFilters={setStartedRemixingFilters}
                  loading={autopilotCuratedSearchLoading}
                  savedFilters={remixedFilters}
                  setSavedFilters={setRemixedFilters}
                  remixContext={remixContext}
                  suggestedPrompts={combinedLLMFeedback}
                />
                {startedPreviewingRemixResults && (
                  <AutopilotPreviewSteps
                    isRemixStep={true}
                    curatedSearchId={remixCuratedSearchId}
                    previewStep={remixPreviewStep}
                    setPreviewStep={setRemixPreviewStep}
                    previewContactFeedback={remixPreviewContactFeedback}
                    setPreviewContactFeedback={setRemixPreviewContactFeedback}
                    existingAutopilotSession={
                      getFragmentData(
                        AutopilotPreviewSteps_AutopilotSessionFragmentDoc,
                        autopilotSessionsData?.autopilotSessions[0],
                      ) ?? null
                    }
                  />
                )}
              </>
            )}
          </CiroContainer>
        </AutopilotCuratedInternalSearchContext.Provider>
      </AutopilotCuratedSearchContext.Provider>
    </AutopilotProvider>
  );
};

export default InternalAutopilotCuratedSearch;
